/**
 * CLIENT-SIDE UX rate limiter.
 *
 * This is an in-memory, per-tab guard used purely to short-circuit obviously
 * abusive input loops in the browser (e.g. repeated failed logins). It is
 * NOT the security boundary and CANNOT be relied on across tabs, devices,
 * or refreshes.
 *
 * The DURABLE, cross-instance rate limit that actually protects the platform
 * lives in `supabase/functions/_shared/rateLimit.ts` (Postgres-backed via
 * the `rate_limit_entries` table). Every edge function that needs enforcement
 * calls `enforceRateLimit(...)` from that module. Supabase Auth also applies
 * its own server-side throttling on signInWithPassword / signUp.
 *
 * If you need horizontally-scalable enforcement, use the edge-function
 * limiter — do not add another client-side implementation here.
 */
interface RateLimiterConfig {
  maxRequests: number;
  windowMs: number;
  keyGenerator?: (identifier: string) => string;
}

interface RequestRecord {
  count: number;
  resetTime: number;
}

class RateLimiter {
  private records: Map<string, RequestRecord> = new Map();
  private config: RateLimiterConfig;
  
  constructor(config: RateLimiterConfig) {
    this.config = {
      keyGenerator: (id: string) => id,
      ...config
    };
    
    // Clean up expired records every minute
    setInterval(() => this.cleanup(), 60000);
  }
  
  private cleanup(): void {
    const now = Date.now();
    for (const [key, record] of this.records.entries()) {
      if (now > record.resetTime) {
        this.records.delete(key);
      }
    }
  }
  
  private getKey(identifier: string): string {
    return this.config.keyGenerator!(identifier);
  }
  
  check(identifier: string): {
    allowed: boolean;
    remaining: number;
    resetTime: number;
    retryAfter?: number;
  } {
    const key = this.getKey(identifier);
    const now = Date.now();
    const record = this.records.get(key);
    
    if (!record || now > record.resetTime) {
      // Create new record
      const newRecord = {
        count: 1,
        resetTime: now + this.config.windowMs
      };
      this.records.set(key, newRecord);
      
      return {
        allowed: true,
        remaining: this.config.maxRequests - 1,
        resetTime: newRecord.resetTime
      };
    }
    
    if (record.count >= this.config.maxRequests) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: record.resetTime,
        retryAfter: record.resetTime - now
      };
    }
    
    // Increment count
    record.count++;
    
    return {
      allowed: true,
      remaining: this.config.maxRequests - record.count,
      resetTime: record.resetTime
    };
  }
  
  reset(identifier: string): void {
    const key = this.getKey(identifier);
    this.records.delete(key);
  }
  
  getStatus(identifier: string): {
    requests: number;
    remaining: number;
    resetTime: number;
  } {
    const key = this.getKey(identifier);
    const record = this.records.get(key);
    const now = Date.now();
    
    if (!record || now > record.resetTime) {
      return {
        requests: 0,
        remaining: this.config.maxRequests,
        resetTime: 0
      };
    }
    
    return {
      requests: record.count,
      remaining: Math.max(0, this.config.maxRequests - record.count),
      resetTime: record.resetTime
    };
  }
}

// Pre-configured rate limiters for different use cases
export const authRateLimiter = new RateLimiter({
  maxRequests: 5, // 5 login attempts
  windowMs: 15 * 60 * 1000, // 15 minutes
  keyGenerator: (email: string) => `auth:${email.toLowerCase()}`
});

export const apiRateLimiter = new RateLimiter({
  maxRequests: 100, // 100 API calls
  windowMs: 60 * 1000, // 1 minute
  keyGenerator: (userId: string) => `api:${userId}`
});

export const uploadRateLimiter = new RateLimiter({
  maxRequests: 10, // 10 uploads
  windowMs: 5 * 60 * 1000, // 5 minutes
  keyGenerator: (userId: string) => `upload:${userId}`
});

export const searchRateLimiter = new RateLimiter({
  maxRequests: 60, // 60 searches
  windowMs: 60 * 1000, // 1 minute
  keyGenerator: (identifier: string) => `search:${identifier}`
});

// Hook for using rate limiters in React components
export const useRateLimit = (rateLimiter: RateLimiter, identifier: string) => {
  const checkLimit = (): {
    allowed: boolean;
    remaining: number;
    retryAfter?: number;
  } => {
    const result = rateLimiter.check(identifier);
    return {
      allowed: result.allowed,
      remaining: result.remaining,
      retryAfter: result.retryAfter
    };
  };
  
  const getStatus = () => rateLimiter.getStatus(identifier);
  const reset = () => rateLimiter.reset(identifier);
  
  return { checkLimit, getStatus, reset };
};

export default RateLimiter;